import * as React from 'react';
import { Autocomplete, Box, Button, Checkbox, Chip, TextField } from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import api from '../../services/api';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export default function EmpresaSelect({ handler, selected = [] }) {
  const [empresas, setEmpresas] = React.useState([]);

  React.useEffect(() => {
    Promise.all([
      api.get('/empresa-athena/list').catch(() => ({ data: [] })),
      api.get('/empresa/all').catch(() => ({ data: [] })),
    ]).then(([athenaRes, horusRes]) => {
      const aMap = new Map();
      athenaRes.data.forEach(e => {
        aMap.set(e.cod_empresa, e.fantasy_name || e.name || String(e.cod_empresa));
      });

      const merged = [];
      const seen = new Set();

      // 1) Empresas que existem no Athena (cadastro completo)
      aMap.forEach((nome, cod) => {
        merged.push({ cod_empresa: cod, nome_fantasia: nome, inAthena: true });
        seen.add(cod);
      });

      // 2) Empresas que existem so no Horus (orfas do Athena)
      horusRes.data
        .filter(e => e.status === 1)
        .forEach(e => {
          if (!seen.has(e.cod_empresa)) {
            merged.push({
              cod_empresa: e.cod_empresa,
              nome_fantasia: e.nome_fantasia || e.razao_social || String(e.cod_empresa),
              inAthena: false,
            });
            seen.add(e.cod_empresa);
          }
        });

      merged.sort((a, b) => Number(a.cod_empresa) - Number(b.cod_empresa));
      setEmpresas(merged);
    });
  }, []);

  const allSelected = selected.length > 0 && selected.length === empresas.length;
  const selectedObjects = empresas.filter(e => selected.includes(e.cod_empresa));
  const orphanCount = empresas.filter(e => !e.inAthena).length;

  const toggleAll = () => {
    if (allSelected) {
      handler([]);
    } else {
      handler(empresas.map(e => e.cod_empresa));
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%' }}>
      <Autocomplete
        multiple
        size="small"
        sx={{ flex: 1 }}
        options={empresas}
        value={selectedObjects}
        disableCloseOnSelect
        limitTags={6}
        getOptionLabel={(opt) => `${opt.cod_empresa} ${opt.nome_fantasia}`}
        isOptionEqualToValue={(o, v) => o.cod_empresa === v.cod_empresa}
        onChange={(_, val) => handler(val.map(v => v.cod_empresa))}
        renderOption={(props, option, { selected: isSel }) => (
          <li {...props} key={option.cod_empresa}>
            <Checkbox icon={icon} checkedIcon={checkedIcon} checked={isSel} sx={{ mr: 1, p: 0.5 }} />
            <span style={{ fontWeight: 600, marginRight: 6 }}>{option.cod_empresa}</span>
            <span style={{ flex: 1 }}>{option.nome_fantasia}</span>
            {!option.inAthena && (
              <Chip label="Sem Athena" size="small" color="warning" variant="outlined" sx={{ ml: 1, fontSize: 10 }} />
            )}
          </li>
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              size="small"
              variant="outlined"
              color={option.inAthena ? 'default' : 'warning'}
              label={`${option.cod_empresa}`}
              title={option.inAthena ? option.nome_fantasia : `${option.nome_fantasia} (sem Athena)`}
              {...getTagProps({ index })}
              key={option.cod_empresa}
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={selected.length === 0 ? 'Buscar empresa por código ou nome…' : ''}
            label={`Empresas (${selected.length}/${empresas.length}${orphanCount > 0 ? ` · ${orphanCount} sem Athena` : ''})`}
          />
        )}
      />
      <Button
        size="small"
        variant={allSelected ? 'contained' : 'outlined'}
        onClick={toggleAll}
        sx={{ whiteSpace: 'nowrap', mt: 0.25 }}
      >
        {allSelected ? 'Limpar' : 'Todas'}
      </Button>
    </Box>
  );
}
